/**
 * Online payment providers, and what a payer is allowed to pay.
 *
 * Two things live here because they are both decisions the payment page has to make and neither
 * belongs to the database: which providers exist and what each needs configured, and how much of
 * an invoice may be settled at once.
 *
 * **Nothing here moves money.** Rent settles into the agency's own provider account — RentorNG is
 * never the merchant of record (ADR-0009). This module only decides what to offer.
 */

import type { Minor } from "./money";

export type GatewayProvider = "paystack" | "flutterwave" | "stripe" | "monnify" | "manual";

export interface GatewayInfo {
  provider: GatewayProvider;
  name: string;
  /** What the payer sees on the button, unless the agency renames it. */
  defaultLabel: string;
  blurb: string;
  /** Currencies the provider settles in. Empty means it will take anything. */
  currencies: string[];
  /** Safe to send to a browser — it is designed to be public. */
  needsPublicKey: boolean;
  /** Server-side only. Never returned by a read endpoint. */
  needsSecretKey: boolean;
  /** Where the agency finds its keys, so nobody has to search for it. */
  keysAt: string;
}

export const GATEWAYS: GatewayInfo[] = [
  {
    provider: "paystack",
    name: "Paystack",
    defaultLabel: "Pay with card or transfer",
    blurb: "Cards, bank transfer and USSD. Settles to your Paystack account.",
    currencies: ["NGN", "GHS", "ZAR", "KES", "USD"],
    needsPublicKey: true,
    needsSecretKey: true,
    keysAt: "dashboard.paystack.com → Settings → API Keys",
  },
  {
    provider: "flutterwave",
    name: "Flutterwave",
    defaultLabel: "Pay with Flutterwave",
    blurb: "Cards, transfer, mobile money and USSD across several countries.",
    currencies: ["NGN", "GHS", "KES", "UGX", "TZS", "ZAR", "USD", "GBP", "EUR"],
    needsPublicKey: true,
    needsSecretKey: true,
    keysAt: "app.flutterwave.com → Settings → API",
  },
  {
    provider: "monnify",
    name: "Monnify",
    defaultLabel: "Pay with Monnify",
    blurb: "Reserved account numbers and transfer, popular for rent collection.",
    currencies: ["NGN"],
    needsPublicKey: true,
    needsSecretKey: true,
    keysAt: "app.monnify.com → Settings → API Keys",
  },
  {
    provider: "stripe",
    name: "Stripe",
    defaultLabel: "Pay by card",
    blurb: "International cards. Useful for tenants paying from abroad.",
    currencies: ["USD", "GBP", "EUR", "ZAR"],
    needsPublicKey: true,
    needsSecretKey: true,
    keysAt: "dashboard.stripe.com → Developers → API keys",
  },
];

export function gatewayInfo(provider: string): GatewayInfo | null {
  return GATEWAYS.find((gateway) => gateway.provider === provider) ?? null;
}

/**
 * Is this provider ready to take money?
 *
 * A gateway that is switched on but has no key would render a button that fails when pressed,
 * which is worse than not offering it — so the payment page asks this before showing anything.
 */
export function gatewayIsUsable(gateway: {
  provider: string;
  enabled: boolean;
  publicKey?: string | null;
  secretKey?: string | null;
}): boolean {
  if (!gateway.enabled) return false;
  const info = gatewayInfo(gateway.provider);
  if (!info) return false;
  if (info.needsPublicKey && !gateway.publicKey) return false;
  if (info.needsSecretKey && !gateway.secretKey) return false;
  return true;
}

/** Why a configured-looking gateway is still not being offered. Shown in settings, not to payers. */
export function gatewayBlockedReason(gateway: {
  provider: string;
  enabled: boolean;
  publicKey?: string | null;
  secretKey?: string | null;
}): string | null {
  if (gatewayIsUsable(gateway)) return null;
  const info = gatewayInfo(gateway.provider);
  if (!info) return "Unknown provider.";
  if (!gateway.enabled) return "Switched off.";
  const missing: string[] = [];
  if (info.needsPublicKey && !gateway.publicKey) missing.push("public key");
  if (info.needsSecretKey && !gateway.secretKey) missing.push("secret key");
  return missing.length ? `Add the ${missing.join(" and ")} — find them at ${info.keysAt}.` : null;
}

/* ---------------------------------------------------------- payment policy */

export type PaymentPolicy = "full" | "part";

export const PAYMENT_POLICIES: Array<{ value: PaymentPolicy; label: string; description: string }> = [
  {
    value: "full",
    label: "Full payment only",
    description: "The payer can only settle the whole outstanding balance. The default.",
  },
  {
    value: "part",
    label: "Part payment allowed",
    description: "The payer chooses an amount. Useful for a tenant paying rent in instalments.",
  },
];

export interface AmountRule {
  /** What the pay page offers by default. */
  suggested: Minor;
  min: Minor;
  max: Minor;
  /** Whether the payer may change the amount at all. */
  editable: boolean;
  /** One sentence explaining the limits, shown under the field. */
  note: string;
}

/**
 * What this invoice will accept.
 *
 * The maximum is always the outstanding balance: over-paying an invoice through a public link is
 * not a kindness, it is a refund request waiting to happen. Over-payment recorded by staff is
 * different — that becomes account credit (ADR-0005).
 */
export function amountRuleFor(
  invoice: { balance: Minor; paymentPolicy: PaymentPolicy; minimumPart: Minor },
  currencyFormat: (amount: Minor) => string,
): AmountRule {
  const balance = Math.max(0, invoice.balance);

  if (invoice.paymentPolicy !== "part") {
    return {
      suggested: balance,
      min: balance,
      max: balance,
      editable: false,
      note: "This invoice is settled in one payment.",
    };
  }

  const min = Math.min(Math.max(0, invoice.minimumPart) || 1, balance);
  return {
    suggested: balance,
    min,
    max: balance,
    editable: true,
    note:
      invoice.minimumPart > 0
        ? `Pay any amount from ${currencyFormat(min)} up to ${currencyFormat(balance)}.`
        : `Pay any amount up to ${currencyFormat(balance)}.`,
  };
}

/** Validate what a payer typed. Returns null when it is acceptable. */
export function checkPayAmount(amount: Minor, rule: AmountRule, currencyFormat: (amount: Minor) => string): string | null {
  if (!Number.isFinite(amount) || amount <= 0) return "Enter an amount.";
  if (amount > rule.max) return `That is more than the ${currencyFormat(rule.max)} outstanding.`;
  if (amount < rule.min) return `The smallest accepted payment is ${currencyFormat(rule.min)}.`;
  return null;
}
